// src/components/Employee/MeetingRoom.jsx
"use client";

import { useEffect, useState, useRef } from "react"
import {
  Mic,
  MicOff,
  Globe,
  Users,
  Clock,
  Calendar,
  MapPin,
  Target,
  X,
  ChevronDown,
  Wifi,
  WifiOff,
  AudioLines,
  Check,
  AlertCircle,
  Bell,
} from "lucide-react"
import { useParams, useNavigate } from 'react-router-dom'
import QuitMeetingPopup from './QuitMeetingPopup'

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`
const AI_SERVICE_URL = (process.env.REACT_APP_AI_SERVICE_URL || "https://your-ai-service.railway.app").replace(/\/+$/, "")
const getToken = () => localStorage.getItem('access_token')

const LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
]

export default function EmployeeMeetingRoom() {
  const navigate = useNavigate()
  const params = useParams()
  const meetingId = params.id

  const [seconds, setSeconds] = useState(0)
  const [recording, setRecording] = useState(false)
  const [meetingDetails, setMeetingDetails] = useState(null)
  const [participants, setParticipants] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [showParticipants, setShowParticipants] = useState(false)
  const [showQuitPopup, setShowQuitPopup] = useState(false)
  const [notification, setNotification] = useState({ show: false, message: "", type: "" })

  const [transcription, setTranscription] = useState("")
  const [interimText, setInterimText] = useState("")
  const [translation, setTranslation] = useState("")
  const [detectedLang, setDetectedLang] = useState('fr')

  const [translationLang, setTranslationLang] = useState('en')
  const [showTranslationLang, setShowTranslationLang] = useState(false)
  const [onlineParticipants, setOnlineParticipants] = useState([])
  const [currentUser, setCurrentUser] = useState(null)

  // Audio test states
  const [isPlayingAudio, setIsPlayingAudio] = useState(false)
  const [audioProgress, setAudioProgress] = useState(0)
  const audioRef = useRef(null)

  // Refs pour la reconnaissance vocale
  const recognitionRef = useRef(null)
  const finalTranscriptRef = useRef("")
  const translationTimeoutRef = useRef(null)
  const autoRestartIntervalRef = useRef(null)

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type })
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" })
    }, 3000)
  }

  useEffect(() => {
    const authData = JSON.parse(localStorage.getItem("auth_data") || "{}")
    setCurrentUser({
      id: authData.userId,
      name: authData.name,
      email: authData.email
    })
  }, [])

  // Audio progress tracking
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    
    const updateProgress = () => {
      if (audio.duration) {
        setAudioProgress((audio.currentTime / audio.duration) * 100)
      }
    }
    
    const handleEnded = () => {
      setIsPlayingAudio(false)
      setAudioProgress(0)
    }
    
    audio.addEventListener('timeupdate', updateProgress)
    audio.addEventListener('ended', handleEnded)
    
    return () => {
      audio.removeEventListener('timeupdate', updateProgress)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [])

  // Participants en ligne (simulation)
  useEffect(() => {
    if (participants.length === 0) return
    const initialOnline = participants.filter(p => p.id === currentUser?.id).map(p => p.id)
    setOnlineParticipants(initialOnline)

    const interval = setInterval(() => {
      if (participants.length > 0 && recording) {
        setOnlineParticipants(prev => {
          const newOnline = [...prev]
          const offlineParticipants = participants.filter(p => !prev.includes(p.id) && p.id !== currentUser?.id)
          if (offlineParticipants.length > 0 && Math.random() > 0.7) {
            const randomIndex = Math.floor(Math.random() * offlineParticipants.length)
            newOnline.push(offlineParticipants[randomIndex].id)
          }
          const onlineNotCurrent = prev.filter(id => id !== currentUser?.id)
          if (onlineNotCurrent.length > 0 && Math.random() > 0.8) {
            const randomIndex = Math.floor(Math.random() * onlineNotCurrent.length)
            const indexToRemove = newOnline.indexOf(onlineNotCurrent[randomIndex])
            if (indexToRemove > -1) newOnline.splice(indexToRemove, 1)
          }
          return newOnline
        })
      }
    }, 10000)
    return () => clearInterval(interval)
  }, [participants, recording, currentUser])

  const detectLanguage = (text) => {
    if (!text) return 'fr'
    if (/[\u0600-\u06FF]/.test(text)) return 'ar'
    if (/[àâäéèêëïîôöùûüÿçœ]/.test(text)) return 'fr'
    if (/[ñáéíóúü¿¡]/.test(text)) return 'es'
    if (/[äöüß]/.test(text)) return 'de'
    if (/[àèéìíòóù]/.test(text)) return 'it'
    return 'en'
  }

  const translateText = async (text, targetLang) => {
    if (!text.trim()) return

    if (translationTimeoutRef.current) {
      clearTimeout(translationTimeoutRef.current)
    }

    translationTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`${AI_SERVICE_URL}/translate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: text.trim(), target_lang: targetLang })
        })

        if (response.ok) {
          const data = await response.json()
          setTranslation(prev => prev + data.translated_text + '\n')
        } else {
          console.error("Erreur traduction:", response.status)
        }
      } catch (error) {
        console.error("Erreur traduction:", error)
      }
    }, 1000)
  }

  useEffect(() => {
    if (meetingId) fetchMeetingData()
  }, [meetingId])

  const fetchMeetingData = async () => {
    try {
      const token = getToken()
      if (!token) {
        setError("Vous devez être connecté")
        setLoading(false)
        return
      }
      const res = await fetch(`${API_URL}/reunions/${meetingId}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) throw new Error('Erreur chargement')
      const data = await res.json()
      setMeetingDetails(data)
      setParticipants(data.participants_details || [])
    } catch (error) {
      setError("Impossible de charger la réunion")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!recording) return
    const interval = setInterval(() => setSeconds(s => s + 1), 1000)
    return () => clearInterval(interval)
  }, [recording])

  const playTestAudio = () => {
    if (audioRef.current) {
      if (isPlayingAudio) {
        audioRef.current.pause()
        setIsPlayingAudio(false)
      } else {
        audioRef.current.play()
        setIsPlayingAudio(true)
      }
    }
  }

  const initSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

    if (!SpeechRecognition) {
      setError("Votre navigateur ne supporte pas la reconnaissance vocale. Utilisez Chrome.")
      return null
    }

    const recognition = new SpeechRecognition()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'fr-FR'
    recognition.maxAlternatives = 1
    
    let restartAttempts = 0
    let lastRestartTime = Date.now()

    recognition.onstart = () => {
      console.log("🎤 Reconnaissance démarrée")
      setError("")
      restartAttempts = 0
      showNotification("Reconnaissance vocale activée - Parlez distinctement", "success")
    }

    recognition.onerror = (event) => {
      console.error("Erreur:", event.error)
      
      if (event.error === 'network' || event.error === 'audio-capture' || event.error === 'no-speech') {
        console.log("🔄 Tentative de redémarrage...")
        
        const now = Date.now()
        if (now - lastRestartTime > 1000) {
          lastRestartTime = now
          restartAttempts++
          
          if (restartAttempts <= 5) {
            setTimeout(() => {
              if (recording && recognitionRef.current) {
                try {
                  recognitionRef.current.start()
                  console.log(`✅ Redémarrage ${restartAttempts}/5 réussi`)
                } catch (e) {
                  console.error("❌ Erreur redémarrage:", e)
                }
              }
            }, 500)
          } else {
            setError("Problème de microphone. Vérifiez vos permissions.")
            showNotification("Problème de microphone", "error")
          }
        }
      } else if (event.error === 'not-allowed') {
        setError("Veuillez autoriser l'accès au microphone")
        showNotification("Microphone non autorisé", "error")
      }
    }

    recognition.onend = () => {
      console.log("Reconnaissance terminée")
      
      if (recording) {
        console.log("🔄 Redémarrage automatique de la reconnaissance...")
        setTimeout(() => {
          if (recording && recognitionRef.current) {
            try {
              recognitionRef.current.start()
              console.log("✅ Reconnaissance redémarrée avec succès")
            } catch (e) {
              console.error("❌ Échec redémarrage:", e)
              setTimeout(() => {
                if (recording && recognitionRef.current) {
                  try {
                    recognitionRef.current = initSpeechRecognition()
                    if (recognitionRef.current) {
                      recognitionRef.current.start()
                    }
                  } catch (err) {
                    console.error("❌ Échec redémarrage final:", err)
                  }
                }
              }, 1000)
            }
          }
        }, 100)
      }
    }

    recognition.onresult = (event) => {
      let final = ''
      let interim = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        const confidence = event.results[i][0].confidence
        
        if (event.results[i].isFinal) {
          final += transcript + ' '
          finalTranscriptRef.current += transcript + ' '
          console.log(`✅ Final (${Math.round(confidence * 100)}%): ${transcript}`)
        } else {
          interim += transcript + ' '
        }
      }

      if (final) {
        let cleanedFinal = final
          .replace(/\s+/g, ' ')
          .trim()
        
        setTranscription(finalTranscriptRef.current)
        const lang = detectLanguage(cleanedFinal)
        if (lang !== detectedLang) setDetectedLang(lang)
        translateText(cleanedFinal, translationLang)
      }

      setInterimText(interim)
      restartAttempts = 0
    }

    return recognition
  }

  const startRecording = async () => {
    try {
      setError("")
      setTranscription("")
      setTranslation("")
      finalTranscriptRef.current = ""

      const token = getToken()
      
      if (!token) {
        setError("Vous n'êtes pas connecté")
        showNotification("Veuillez vous reconnecter", "error")
        return
      }

      if (autoRestartIntervalRef.current) {
        clearInterval(autoRestartIntervalRef.current)
      }
      
      const recognition = initSpeechRecognition()
      if (recognition) {
        recognitionRef.current = recognition
        recognition.start()
        setRecording(true)
        setSeconds(0)
        
        autoRestartIntervalRef.current = setInterval(() => {
          if (recording && recognitionRef.current) {
            console.log("🔄 Restart périodique de la reconnaissance...")
            try {
              recognitionRef.current.stop()
              setTimeout(() => {
                if (recording && recognitionRef.current) {
                  try {
                    recognitionRef.current.start()
                  } catch (e) {
                    console.error("Erreur restart périodique:", e)
                  }
                }
              }, 100)
            } catch (e) {
              console.error("Erreur arrêt recognition:", e)
            }
          }
        }, 45000)
      }
    } catch (error) {
      console.error("Erreur démarrage:", error)
      setError("Impossible de démarrer: " + error.message)
      showNotification("Erreur: " + error.message, "error")
    }
  }

  const stopRecording = () => {
    setRecording(false)
    
    if (autoRestartIntervalRef.current) {
      clearInterval(autoRestartIntervalRef.current)
      autoRestartIntervalRef.current = null
    }
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop()
      } catch (e) { }
      recognitionRef.current = null
    }
    
    if (translationTimeoutRef.current) {
      clearTimeout(translationTimeoutRef.current)
    }
    
    showNotification("Enregistrement arrêté", "info")
  }

  const changeTranslationLang = (newLang) => {
    setTranslationLang(newLang)
    setShowTranslationLang(false)
    setTranslation("")
  }

  const handleQuit = () => setShowQuitPopup(true)

  const handleConfirmQuit = () => {
    stopRecording()
    setShowQuitPopup(false)
    navigate("/")
  }

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop()
        } catch (e) { }
      }
      if (translationTimeoutRef.current) {
        clearTimeout(translationTimeoutRef.current)
      }
      if (autoRestartIntervalRef.current) {
        clearInterval(autoRestartIntervalRef.current)
      }
    }
  }, [])

  if (loading) return (
    <div style={styles.loadingContainer}>
      <div style={styles.loadingSpinner}></div>
      <div style={styles.loadingText}>Chargement de la réunion...</div>
    </div>
  )

  const currentLang = LANGUAGES.find(l => l.code === detectedLang) || LANGUAGES[0]
  const targetLang = LANGUAGES.find(l => l.code === translationLang) || LANGUAGES[1]

  return (
    <div style={styles.container}>
      {/* Notification Toast */}
      {notification.show && (
        <div style={{
          ...styles.notification,
          backgroundColor: notification.type === 'success' ? '#22c55e' : notification.type === 'error' ? '#ef4444' : '#3b82f6',
          animation: 'slideIn 0.3s ease-out'
        }}>
          {notification.type === 'success' && <Check size={18} />}
          {notification.type === 'error' && <AlertCircle size={18} />}
          {notification.type === 'info' && <Bell size={18} />}
          <span>{notification.message}</span>
        </div>
      )}

      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.titleSection}>
            <h1 style={styles.title}>{meetingDetails?.titre}</h1>
            {recording && (
              <div style={styles.recordingBadge}>
                <div style={styles.recordingDot}></div>
                <span>Enregistrement en cours</span>
              </div>
            )}
          </div>
          <div style={styles.headerButtons}>
            {!recording ? (
              <button style={styles.headerBtnGreen} onClick={startRecording}>
                <Mic size={16} /> Démarrer
              </button>
            ) : (
              <button style={styles.headerBtnRed} onClick={stopRecording}>
                <MicOff size={16} /> Stop
              </button>
            )}
            <button style={styles.headerBtnQuit} onClick={handleQuit}>
              <X size={16} /> Quitter
            </button>
          </div>
        </div>

        <div style={styles.meetingInfo}>
          <span style={styles.infoItem}>
            <Target size={14} color="#5e17eb" /> {meetingDetails?.objectif || "Objectif non spécifié"}
          </span>
          <span style={styles.infoItem}>
            <Calendar size={14} color="#5e17eb" /> {meetingDetails?.date_heure ? new Date(meetingDetails.date_heure).toLocaleString('fr-FR', {
              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
            }) : 'Date non spécifiée'}
          </span>
          <span style={styles.infoItem}>
            <Clock size={14} color="#5e17eb" /> {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
          </span>
          <span style={styles.infoItem}>
            <MapPin size={14} color="#5e17eb" /> {meetingDetails?.salle || "Salle non spécifiée"}
          </span>

          <div style={styles.participantsHeaderCompact}>
            <button style={styles.participantsButton} onClick={() => setShowParticipants(!showParticipants)}>
              <Users size={16} color="#5e17eb" />
              <span style={styles.participantsCountCompact}>{participants.length}</span>
              <ChevronDown size={14} style={{ transform: showParticipants ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
            </button>

            {showParticipants && (
              <div style={styles.participantsDropdown}>
                <div style={styles.participantsDropdownHeader}>
                  <Users size={16} color="#5e17eb" />
                  <span>Participants ({participants.length})</span>
                  <span style={{ fontSize: 11, color: "#666", marginLeft: "auto" }}>
                    <Wifi size={12} /> Connectés: {onlineParticipants.length}
                  </span>
                </div>
                <div style={styles.participantsDropdownList}>
                  {participants.map((p, i) => {
                    const isOnline = onlineParticipants.includes(p.id)
                    const isCurrentUser = p.id === currentUser?.id
                    return (
                      <div key={i} style={styles.participantDropdownItem}>
                        <div style={styles.participantDropdownAvatar}>
                          {p.name?.charAt(0).toUpperCase()}
                        </div>
                        <div style={styles.participantDropdownInfo}>
                          <span style={styles.participantDropdownName}>
                            {p.name}
                            {isCurrentUser && <span style={{ fontSize: 10, color: "#5e17eb", marginLeft: 5 }}>(Vous)</span>}
                          </span>
                          <span style={styles.participantDropdownEmail}>{p.email}</span>
                        </div>
                        <div style={styles.participantDropdownStatus}>
                          {isOnline ? (
                            <span style={{ display: "flex", alignItems: "center", gap: 4, color: "#22c55e" }}>
                              <Wifi size={12} /> <span style={{ fontSize: 10 }}>En ligne</span>
                            </span>
                          ) : (
                            <span style={{ display: "flex", alignItems: "center", gap: 4, color: "#9ca3af" }}>
                              <WifiOff size={12} /> <span style={{ fontSize: 10 }}>Hors ligne</span>
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* TEST AUDIO SECTION - FOR COMMISSION TESTING */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 16,
        padding: '16px 24px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
        animation: 'slideIn 0.5s ease-out'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            background: 'white',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20
          }}>
            🎵
          </div>
          <div>
            <div style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>
              🧪 Mode Test - Audio Intégré
            </div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>
              Cliquez sur "Jouer" pour tester la transcription avec cet audio de réunion test
            </div>
          </div>
        </div>
        
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ 
            background: 'rgba(255,255,255,0.2)', 
            borderRadius: 20,
            height: 6,
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${audioProgress}%`,
              height: '100%',
              background: 'white',
              transition: 'width 0.1s linear'
            }} />
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={playTestAudio}
            style={{
              background: 'white',
              border: 'none',
              borderRadius: 40,
              padding: '8px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              color: '#667eea',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={e => e.target.style.transform = 'scale(1.05)'}
            onMouseLeave={e => e.target.style.transform = 'scale(1)'}
          >
            {isPlayingAudio ? '⏸️ Pause' : '▶️ Jouer le test audio'}
          </button>
          
          <audio
            ref={audioRef}
            src="/audio/test-meeting.mp3"
            preload="auto"
          />
        </div>
        
        <div style={{ 
          background: 'rgba(255,255,255,0.15)', 
          padding: '4px 12px', 
          borderRadius: 20,
          fontSize: 11,
          color: 'white'
        }}>
          💡 Conseil: Démarrez l'enregistrement puis jouez l'audio
        </div>
      </div>

      {error && <div style={styles.errorToast}>{error}</div>}

      <div style={styles.mainArea}>
        <div style={styles.leftPanel}>
          <div style={styles.panelHeader}>
            <div style={styles.panelTitle}>
              <AudioLines size={20} color="#5e17eb" />
              <span>TRANSCRIPTION EN TEMPS RÉEL</span>
            </div>
            <div style={styles.langBadge}>
              <span>{currentLang.flag}</span>
              <span>{currentLang.name}</span>
            </div>
          </div>

          <div style={styles.transcriptionArea}>
            {transcription || interimText ? (
              <>
                <div style={styles.transcriptText}>{transcription}</div>
                {interimText && <div style={styles.interimText}>{interimText}</div>}
              </>
            ) : (
              <div style={styles.placeholderText}>
                {recording ? "Parlez... la transcription apparaît en temps réel" : "Cliquez sur Démarrer pour commencer"}
              </div>
            )}
          </div>
        </div>

        <div style={styles.rightPanel}>
          <div style={styles.panelHeader}>
            <div style={styles.panelTitle}>
              <Globe size={20} color="#5e17eb" />
              <span>TRADUCTION</span>
            </div>
            <div style={styles.langSelector}>
              <button style={styles.langButton} onClick={() => setShowTranslationLang(!showTranslationLang)}>
                <span>{targetLang.flag}</span>
                <span>{targetLang.name}</span>
              </button>
              {showTranslationLang && (
                <div style={styles.langDropdown}>
                  {LANGUAGES.map(lang => (
                    <div key={lang.code} style={styles.langOption} onClick={() => changeTranslationLang(lang.code)}>
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <textarea
            style={styles.translationArea}
            value={translation}
            readOnly
            placeholder="La traduction apparaîtra ici en temps réel..."
          />
        </div>
      </div>

      {/* Instruction Tooltip for Testing */}
      <div style={{
        position: 'fixed',
        bottom: 20,
        left: 20,
        background: '#f0f9ff',
        borderLeft: `4px solid #667eea`,
        padding: '12px 16px',
        borderRadius: 12,
        fontSize: 12,
        color: '#334155',
        maxWidth: 280,
        zIndex: 999,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>📋 Pour tester l'application:</div>
        <div>1️⃣ Cliquez "Démarrer" (microphone)</div>
        <div>2️⃣ Cliquez "Jouer le test audio"</div>
        <div>3️⃣ Observez la transcription en temps réel</div>
        <div>4️⃣ La traduction apparaît automatiquement</div>
      </div>

      <QuitMeetingPopup isOpen={showQuitPopup} onClose={() => setShowQuitPopup(false)} onConfirm={handleConfirmQuit} meetingTitle={meetingDetails?.titre} />

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.2); opacity: 0.5; }
        }
      `}</style>
    </div>
  )
}

const styles = {
  container: { background: "#f7f6ff", minHeight: "100vh", padding: 20, fontFamily: "'Inter', sans-serif" },
  notification: {
    position: "fixed",
    top: 20,
    right: 20,
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 20px",
    borderRadius: 12,
    color: "white",
    fontSize: 14,
    fontWeight: 500,
    boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
  },
  header: { background: "white", padding: "20px 24px", borderRadius: 20, marginBottom: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.05)", transition: "all 0.3s ease" },
  headerTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 15 },
  titleSection: { display: "flex", alignItems: "center", gap: 12 },
  title: { color: "#5e17eb", fontSize: 22, margin: 0, fontWeight: 600 },
  recordingBadge: { display: "flex", alignItems: "center", gap: 8, background: "#fee2e2", padding: "4px 12px", borderRadius: 20, color: "#dc2626", fontSize: 12, fontWeight: 500 },
  recordingDot: { width: 8, height: 8, borderRadius: "50%", backgroundColor: "#dc2626", animation: "pulse 1s infinite" },
  headerButtons: { display: "flex", gap: 8 },
  headerBtnGreen: { padding: "8px 20px", borderRadius: 40, background: "#22c55e", color: "white", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 500, transition: "all 0.2s ease", ":hover": { transform: "scale(1.02)", background: "#16a34a" } },
  headerBtnRed: { padding: "8px 20px", borderRadius: 40, background: "#f44336", color: "white", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 500, transition: "all 0.2s ease", ":hover": { transform: "scale(1.02)", background: "#dc2626" } },
  headerBtnQuit: { padding: "8px 20px", borderRadius: 40, background: "#f0f0f0", color: "#666", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 500, transition: "all 0.2s ease", ":hover": { background: "#e5e5e5" } },
  meetingInfo: { display: "flex", gap: 24, flexWrap: "wrap", padding: "12px 0", borderTop: "1px solid #f0f0f0", alignItems: "center" },
  infoItem: { display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#444" },
  participantsHeaderCompact: { position: "relative", marginLeft: "auto" },
  participantsButton: { display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", background: "#ede9ff", border: "none", borderRadius: 30, cursor: "pointer", color: "#5e17eb", fontSize: 13, fontWeight: 500, transition: "all 0.2s ease", ":hover": { background: "#e0d9ff" } },
  participantsCountCompact: { background: "#5e17eb", color: "white", padding: "2px 8px", borderRadius: 20, fontSize: 11, marginLeft: 4 },
  participantsDropdown: { position: "absolute", top: "100%", right: 0, marginTop: 8, background: "white", borderRadius: 16, boxShadow: "0 10px 40px rgba(0,0,0,0.15)", zIndex: 1000, minWidth: 320, border: "1px solid #ede9ff", animation: "slideIn 0.2s ease-out" },
  participantsDropdownHeader: { padding: "16px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 8, fontWeight: 600, color: "#333" },
  participantsDropdownList: { maxHeight: 300, overflowY: "auto", padding: "8px" },
  participantDropdownItem: { display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 12, transition: "all 0.2s ease", ":hover": { background: "#f8f7ff" } },
  participantDropdownAvatar: { width: 32, height: 32, borderRadius: 32, background: "#5e17eb", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600 },
  participantDropdownInfo: { flex: 1, display: "flex", flexDirection: "column" },
  participantDropdownName: { fontSize: 13, fontWeight: 500, color: "#333" },
  participantDropdownEmail: { fontSize: 11, color: "#666" },
  participantDropdownStatus: { display: "flex", alignItems: "center", minWidth: 90 },
  mainArea: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, height: "calc(100vh - 280px)" },
  leftPanel: { background: "white", borderRadius: 24, padding: 24, display: "flex", flexDirection: "column", gap: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.05)", height: "100%", transition: "all 0.3s ease" },
  rightPanel: { background: "white", borderRadius: 24, padding: 24, display: "flex", flexDirection: "column", gap: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.05)", height: "100%", transition: "all 0.3s ease" },
  panelHeader: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  panelTitle: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: "#333", letterSpacing: "1px" },
  langBadge: { padding: "4px 10px", borderRadius: 20, backgroundColor: '#f0f0f0', color: '#666', display: "flex", alignItems: "center", gap: 4, fontSize: 12 },
  transcriptionArea: { flex: 1, background: "#f9f9ff", borderRadius: 16, padding: 20, overflowY: "auto", border: "1px solid #e5e7eb", fontFamily: "monospace", fontSize: 14, lineHeight: 1.6 },
  translationArea: { flex: 1, padding: 20, border: "1px solid #e5e7eb", borderRadius: 16, fontSize: 14, fontFamily: "monospace", lineHeight: 1.6, resize: "none", background: "#f9f9ff", color: "#333" },
  transcriptText: { whiteSpace: "pre-wrap", color: "#333" },
  interimText: { color: "#999", fontStyle: "italic", marginTop: 10 },
  placeholderText: { color: "#999", textAlign: "center", marginTop: 150, fontSize: 14 },
  langSelector: { position: "relative" },
  langButton: { padding: "6px 12px", background: "#f0f0f0", border: "none", borderRadius: 40, display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer", transition: "all 0.2s ease", ":hover": { background: "#e5e5e5" } },
  langDropdown: { position: "absolute", top: "100%", right: 0, background: "white", borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", marginTop: 5, zIndex: 10, minWidth: 160, border: "1px solid #eee", animation: "slideIn 0.2s ease-out" },
  langOption: { padding: "10px 16px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, transition: "all 0.2s ease", ":hover": { background: "#f5f5f5" } },
  errorToast: { position: "fixed", bottom: 20, right: 20, background: "#f44336", color: "white", padding: "12px 24px", borderRadius: 100, fontSize: 14, boxShadow: "0 4px 12px rgba(244,67,54,0.3)", zIndex: 1000, animation: "slideIn 0.3s ease-out" },
  loadingContainer: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f7f6ff" },
  loadingSpinner: { width: 50, height: 50, border: "3px solid #e0e0e0", borderTop: "3px solid #5e17eb", borderRadius: "50%", animation: "spin 1s linear infinite", marginBottom: 20 },
  loadingText: { color: "#666", fontSize: 16 },
}

const styleSheet = document.createElement("style")
styleSheet.textContent = `
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
  @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  @keyframes pulse { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(1.2); opacity: 0.5; } }
`
document.head.appendChild(styleSheet)